import React, { useState } from 'react';
import { MagnifyingGlass, SidebarSimple, CaretLeft, SignOut } from '../../design-system/icons';
import { Link } from '@tanstack/react-router';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SimpleTooltip } from '../ui/tooltip';

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  count?: number;
  hide?: boolean;
  isPlaceholder?: boolean;
  /** If provided, renders as a Link from @tanstack/react-router instead of a button */
  href?: string;
  /** Extra path prefixes that mark this item active, beyond `href`. Use when a
   *  section owns routes that don't share the href prefix — e.g. a Campaigns
   *  item (href `/campaigns`) that must also light up on the singular
   *  campaign-detail route `/campaign/<slug>`. Matched via `startsWith`. */
  activePaths?: string[];
  /** Visual indentation for sub-items */
  indent?: boolean;
  /** Renders a horizontal divider bar directly above this item (Figma nav group split). */
  dividerAbove?: boolean;
}

export interface SidebarPrimaryAction {
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  onClick: () => void;
}

export interface AppSidebarProps {
  navItems: NavItem[];
  activeTab: string;
  /** Called when a button-type nav item is clicked. tabId matches item.id. */
  onTabChange?: (tabId: string) => void;
  onNavigateHome: () => void;
  userInitials: string;
  /** undefined renders a skeleton while profile data is loading. */
  userName: string | undefined;
  /** undefined renders a skeleton while profile data is loading. */
  userRole: string | undefined;
  onNavigateProfile: () => void;
  onSignOut: () => void;
  /** Back navigation callback (optional) */
  onNavigateBack?: () => void;
  backLabel?: string;
  onRefresh?: () => void;
  /** URL to the full wordmark logo (required) */
  logoSrc: string;
  /** URL to the mark-only logo shown when collapsed (falls back to logoSrc) */
  logoMarkSrc?: string;
  /** Display name shown next to the logo */
  appName: string;

  // ── Collapsible (Figma "Expand Nav" / "Collabs Nav") ──────────────────────
  /** Collapsed = icons-only rail (64px). Owned by AppLayout so it can offset main. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Attached to the logo button, which doubles as the expand toggle. */
  expandToggleRef?: React.Ref<HTMLButtonElement>;
  /** Attached to the rail container so the narrow overlay can trap Tab focus in it. */
  overlayRef?: React.Ref<HTMLElement>;

  // ── Optional Figma chrome (apps opt in; admin can omit) ───────────────────
  /** Renders the search field/button when provided. */
  onSearch?: () => void;
  searchPlaceholder?: string;
  /** Primary CTA pinned above the footer (e.g. "Create Campaign"). */
  primaryAction?: SidebarPrimaryAction;
  /** Secondary nav rendered in the footer (e.g. Settings, Help Centre). */
  footerItems?: NavItem[];
}

/**
 * App sidebar — collapsible icon rail (64px) ↔ expanded (224px), Figma "Expand
 * Nav" / "Collabs Nav".
 *
 * Collapse model (shadcn-style, replicated for smoothness): EVERY row uses one
 * pattern — a fixed 40px icon zone on the left (the glyph is centred, so it
 * lands at the same 32px in both states and NEVER moves) followed by a label
 * that simply CLIPS as the rail narrows (overflow-hidden). No opacity fades, no
 * justify-content / padding swaps, no conditional text. Only the rail width
 * animates (200ms ease-linear), which clips/reveals every label uniformly.
 */
const EASE = 'ease-[cubic-bezier(0.32,0.72,0,1)]';
const RAIL_TRANSITION = `transition-[width] duration-[260ms] ${EASE} will-change-[width]`;
/** Fixed icon zone — 40px, glyph centred → 32px (centre of the 64px rail).
 *  Also exactly fills the row (64 − 2×12 padding = 40), so nothing overflows. */
const ICON_ZONE = 'flex w-10 shrink-0 items-center justify-center';

export function AppSidebar(props: AppSidebarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const collapsed = !!props.collapsed;
  const markSrc = props.logoMarkSrc ?? props.logoSrc;
  // Labels fade in/out (in addition to clipping) on the same curve as the rail.
  const labelFade = `transition-opacity duration-[260ms] ${EASE} ${collapsed ? 'opacity-0' : 'opacity-100'}`;

  // Collapsed: clicking the empty rail (anywhere that isn't a real control)
  // expands it, so users aren't forced to hit the small toggle. Buttons/links
  // keep their own behaviour; the toggle + logo stay the keyboard path.
  const handleRailExpand = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, [role="button"]')) return;
    props.onToggleCollapse?.();
  };

  // ── Nav item (shared by primary nav + footer items) ─────────────────────────
  const renderNavItem = (tab: NavItem) => {
    if (tab.hide) return null;
    const isActive = props.activeTab === tab.id;
    const TabIcon = tab.icon;

    const className = [
      'relative flex h-10 w-full shrink-0 cursor-pointer items-center overflow-hidden rounded-lg',
      'border-none text-left font-sans text-body-md-regular font-medium no-underline transition-colors duration-150',
      // Active = #ECEBE9 (stone-200) pill + #171717 (foreground) icon/label. Inactive = muted.
      isActive
        ? 'bg-stone-200 text-foreground'
        : 'text-muted-foreground hover:bg-stone-200/60 hover:text-foreground',
    ].join(' ');

    const inner = (
      <>
        <span className={ICON_ZONE}>
          <TabIcon size={20} strokeWidth={1.6} />
        </span>
        <span className={`pointer-events-none whitespace-nowrap leading-none ${labelFade}`}>{tab.label}</span>
        {tab.count !== undefined && tab.count > 0 ? (
          <>
            <span className="ml-auto mr-3 inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-caption-medium text-white">
              {tab.count}
            </span>
            {collapsed && <span className="absolute top-1.5 right-2 size-2 rounded-full bg-destructive" />}
          </>
        ) : null}
      </>
    );

    let node: React.ReactNode;
    if (tab.href && !tab.isPlaceholder) {
      node = (
        <Link key={tab.id} to={tab.href} className={className}>
          {inner}
        </Link>
      );
    } else {
      // Plain <button> (NOT shadcn Button — its cn()/twMerge injects padding/
      // justify and breaks the shared layout, e.g. the Integration placeholder).
      node = (
        <button
          key={tab.id}
          type="button"
          className={className}
          style={tab.isPlaceholder ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
          onClick={(e) => {
            if (tab.isPlaceholder) {
              e.preventDefault();
              return;
            }
            props.onTabChange?.(tab.id);
          }}
        >
          {inner}
        </button>
      );
    }

    // Collapsed icons need a label tooltip (avoid mystery-meat nav).
    const tip = tab.isPlaceholder ? `${tab.label} · Coming soon` : collapsed ? tab.label : null;
    const item = tip ? (
      <SimpleTooltip content={tip} side="right">
        {node}
      </SimpleTooltip>
    ) : (
      node
    );

    // Figma nav-group split: a thin bar above the item (e.g. above Details, so
    // Details + Settings read as a bottom cluster). The -mt-1 pulls it toward
    // the item so the divider hugs the group it introduces rather than sitting
    // dead-centre in the gap-2 stack.
    if (tab.dividerAbove) {
      return (
        <React.Fragment key={tab.id}>
          <div className="my-1 h-px w-full shrink-0 bg-border" aria-hidden />
          {item}
        </React.Fragment>
      );
    }
    // Non-fragment path still needs the key on the item itself.
    return <React.Fragment key={tab.id}>{item}</React.Fragment>;
  };

  return (
    <aside
      ref={props.overlayRef}
      className={[
        'fixed inset-y-0 left-0 z-20 flex h-screen flex-col overflow-hidden',
        'border-r border-border bg-stone-50',
        RAIL_TRANSITION,
        collapsed ? 'w-[64px] cursor-pointer' : 'w-56',
      ].join(' ')}
      aria-label="Application navigation"
      onClick={collapsed && props.onToggleCollapse ? handleRailExpand : undefined}
      // Public (no-login) share view: the shell intercepts every click that
      // isn't inside a `[data-public-allowed]` subtree and turns it into a
      // login prompt. Collapsing/expanding the rail is pure layout — no data,
      // no navigation — so the click-to-expand CONTAINER is exempt while
      // collapsed (that's when a bare rail click expands it). Inert everywhere
      // else. See campaign-detail/PublicCampaignShell.tsx.
      {...(collapsed && props.onToggleCollapse ? { 'data-public-allowed': '' } : {})}
    >
      {/* ── Logo + collapse control ─────────────────────────────────────────
          Collapsed: the logo mark doubles as the expand toggle — on hover the
          mark swaps to the sidebar icon, click expands (the toggle lives BEHIND
          the logo; the 72px rail has no room for a separate button).
          Expanded: the logo is just the logo (→ home); a dedicated, always-
          visible collapse button sits on the right of the header. */}
      <div className="flex h-[52px] shrink-0 items-center overflow-hidden px-3">
        <button
          type="button"
          ref={props.expandToggleRef}
          onClick={collapsed ? (props.onToggleCollapse ?? props.onNavigateHome) : props.onNavigateHome}
          className="group/logo relative flex w-10 shrink-0 items-center justify-center"
          aria-label={collapsed && props.onToggleCollapse ? 'Expand sidebar' : props.appName}
          // Exempt from the public share view's click interception ONLY while
          // it is the expand toggle; expanded it navigates home, which must
          // still prompt login.
          {...(collapsed && props.onToggleCollapse ? { 'data-public-allowed': '' } : {})}
        >
          <img
            src={markSrc}
            alt={props.appName}
            className={`size-8 shrink-0 object-contain transition-opacity duration-150 ${collapsed && props.onToggleCollapse ? 'group-hover/logo:opacity-0' : ''}`}
          />
          {collapsed && props.onToggleCollapse && (
            <SidebarSimple
              size={20}
              strokeWidth={1.6}
              className="absolute text-foreground opacity-0 transition-opacity duration-150 group-hover/logo:opacity-100"
            />
          )}
        </button>
        <button
          type="button"
          onClick={props.onNavigateHome}
          tabIndex={collapsed ? -1 : 0}
          aria-label={props.appName}
          className={`min-w-0 flex-1 overflow-hidden text-left text-[20px] leading-none font-bold tracking-[-0.01em] whitespace-nowrap text-foreground ${labelFade}`}
        >
          {props.appName}
        </button>
        {/* Expanded only: visible collapse button, pinned to the right. */}
        {!collapsed && props.onToggleCollapse && (
          <button
            type="button"
            onClick={props.onToggleCollapse}
            aria-label="Collapse sidebar"
            // Exempt from the public share view's click interception — pure
            // layout. See the aside's `data-public-allowed` comment above.
            data-public-allowed=""
            className={`flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-stone-200 hover:text-foreground ${labelFade}`}
          >
            <SidebarSimple size={20} strokeWidth={1.6} />
          </button>
        )}
      </div>

      {/* ── Search — single bordered field; clips to its magnifier collapsed ── */}
      {props.onSearch &&
        (() => {
          const searchBtn = (
            <button
              type="button"
              onClick={props.onSearch}
              aria-label="Search"
              className="flex h-10 w-full items-center overflow-hidden rounded-lg border border-border bg-muted text-muted-foreground transition-colors hover:border-foreground/30"
            >
              <span className={ICON_ZONE}>
                <MagnifyingGlass size={18} weight="regular" />
              </span>
              <span className={`whitespace-nowrap text-body-sm-regular ${labelFade}`}>{props.searchPlaceholder ?? 'Search'}</span>
              <kbd className={`mr-3 ml-auto rounded-md border border-border bg-card px-1.5 py-0.5 text-caption-regular text-muted-foreground ${labelFade}`}>
                ⌘K
              </kbd>
            </button>
          );
          return (
            <div className="px-3 pb-2">
              {collapsed ? (
                <SimpleTooltip content={props.searchPlaceholder ?? 'Search'} side="right">
                  {searchBtn}
                </SimpleTooltip>
              ) : (
                searchBtn
              )}
            </div>
          );
        })()}

      {/* ── Optional back link ─────────────────────────────────────────────── */}
      {props.onNavigateBack && !collapsed && (
        <div className="px-3 pb-1">
          <button
            type="button"
            onClick={props.onNavigateBack}
            className="flex h-9 w-full items-center overflow-hidden rounded-lg text-body-md-regular font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className={ICON_ZONE}>
              <CaretLeft size={20} strokeWidth={1.5} />
            </span>
            <span className="whitespace-nowrap">Back{props.backLabel ? ` to ${props.backLabel}` : ''}</span>
          </button>
        </div>
      )}

      {/* ── Primary nav ──────────────────────────────────────────────────────
          Figma: 24px gap between the logo and the first nav item (brand block
          pb 16px + container gap 8px). The logo is centred in the 52px header
          (~10px slack below the glyph), so pt-3.5 (14px) lands the visible gap
          at ~24px. */}
      <nav className="flex flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto px-3 pt-3.5 pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden">
        {props.navItems.map(renderNavItem)}
      </nav>

      {/* ── Footer: primary CTA + secondary nav + account ──────────────────── */}
      <div className="mt-auto flex shrink-0 flex-col gap-4 p-3">
        {props.primaryAction &&
          (() => {
            const Icon = props.primaryAction.icon;
            const btn = (
              <button
                type="button"
                onClick={props.primaryAction.onClick}
                className="relative flex h-10 w-full items-center overflow-hidden rounded-full bg-primary text-body-sm-medium text-white transition-colors hover:bg-primary/90"
              >
                <span className={ICON_ZONE}>
                  <Icon size={18} strokeWidth={1.8} />
                </span>
                <span className={`whitespace-nowrap ${labelFade}`}>{props.primaryAction.label}</span>
              </button>
            );
            return collapsed ? (
              <SimpleTooltip content={props.primaryAction.label} side="right">
                {btn}
              </SimpleTooltip>
            ) : (
              btn
            );
          })()}

        {props.footerItems?.map(renderNavItem)}

        {/* Account chip — avatar in the fixed icon zone, name/role clips */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative mt-1 flex h-10 w-full items-center overflow-hidden rounded-xl bg-muted transition-colors hover:bg-accent"
              aria-label="Account menu"
            >
              <span className={ICON_ZONE}>
                <span className="flex size-9 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-caption-medium text-stone-700">
                  {props.userInitials}
                </span>
              </span>
              <div className={`flex min-w-0 flex-1 flex-col pr-2 text-left whitespace-nowrap ${labelFade}`}>
                {props.userName !== undefined ? (
                  <span className="truncate text-body-sm-semibold leading-tight text-foreground">
                    {props.userName}
                  </span>
                ) : (
                  <div className="h-4 w-24 animate-pulse rounded-sm bg-muted" />
                )}
                {props.userRole !== undefined ? (
                  <span className="mt-0.5 truncate text-caption-regular text-muted-foreground">
                    {props.userRole}
                  </span>
                ) : (
                  <div className="mt-1 h-3 w-16 animate-pulse rounded-sm bg-muted" />
                )}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="z-30 w-52 p-1">
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-body-sm-regular text-foreground hover:bg-accent"
              onClick={() => {
                setPopoverOpen(false);
                props.onNavigateProfile();
              }}
            >
              View profile
            </button>
            <div className="my-1 h-px bg-muted" />
            <div className="flex items-center gap-2 px-3 py-1">
              <Link to="/privacy-policy" className="text-caption-regular text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
              <span className="text-caption-regular text-muted-foreground">·</span>
              <Link to="/terms" className="text-caption-regular text-muted-foreground hover:text-foreground">
                Terms
              </Link>
            </div>
            <div className="my-1 h-px bg-muted" />
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-body-sm-regular text-rose-600 hover:bg-rose-50"
              onClick={() => {
                setPopoverOpen(false);
                void props.onSignOut();
              }}
            >
              <SignOut size={14} />
              Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </aside>
  );
}
